import { useMemo } from 'react'
import {
  useTaxonomyTranslationFieldTranslations,
  useTaxonomyTranslationReferenceFields,
} from '@global/hooks/queries/taxonomy-translations'
import {
  FieldTranslationInterface,
  FieldType,
  TranslationStatus,
} from '@taxonomy-translation/types'
import { isListField, isListTranslation } from '@taxonomy-translation/utils'

export interface TableDataChild {
  index: number
  parent_id: string
  parent_status: TranslationStatus
  status: TranslationStatus
  confidence: number
  matchDescription: string | null
  channel: string | null
  children_candidate: string | null
  children_candidate_abbrev: string | null
  children_reference: string | null
  children_reference_abbrev: string | null
  children_translated: string | null
  children_list_options: { label: string; value: string }[]
}

export interface TableData extends FieldTranslationInterface<FieldType> {
  count: number | null
  fieldOptions: { label: string; value: string }[]
  __children?: TableDataChild[]
}

export const useTableData = (translationUuid: string) => {
  const {
    data = [],
    isFetching: isTranslationFetching,
    isLoading: isTranslationLoading,
  } = useTaxonomyTranslationFieldTranslations(translationUuid)
  const {
    data: referenceFields = [],
    isFetching: isReferenceFieldsFetching,
    isLoading: isReferenceFieldsLoading,
  } = useTaxonomyTranslationReferenceFields(translationUuid)

  const channelOptionsMap = useMemo(() => {
    const map = new Map<string | null, string[]>()

    for (const field of referenceFields) {
      const channelFields = map.get(field.channel) || []
      channelFields.push(field.name)
      map.set(field.channel, channelFields)
    }

    return map
  }, [referenceFields])

  const tableData = useMemo(() => {
    if (!data) {
      return []
    }

    return data.map(item => {
      const values = [
        ...(channelOptionsMap.get(item.channel) || []),
        ...(item.channel !== null ? channelOptionsMap.get(null) || [] : []),
      ]

      if (item.candidate && !values.includes(item.candidate)) {
        values.push(item.candidate)
      }

      const fieldOptions = values.map(v => ({ value: v, label: v }))

      if (!isListTranslation(item)) {
        return {
          ...item,
          fieldOptions,
          count: null,
        } as TableData
      }

      const referenceField = item.translated
        ? referenceFields.find(
            field => field.name === item.translated && field.channel === item.channel,
          )
        : null

      const listOptions =
        referenceField && isListField(referenceField)
          ? referenceField.listValues.map(([label]) => label).filter((v): v is string => !!v)
          : []

      const children = item.listValueTranslations.map(
        ({
          candidate,
          reference,
          translated,
          candidateAbbrev,
          referenceAbbrev,
          ...rest
        }): TableDataChild => ({
          ...rest,
          children_candidate: candidate,
          children_candidate_abbrev: candidateAbbrev,
          children_reference: reference,
          children_reference_abbrev: referenceAbbrev,
          children_translated: translated,
          children_list_options: [
            ...listOptions,
            ...(candidate && !listOptions.includes(candidate) ? [candidate] : []),
          ].map(v => ({ value: v, label: v })),
          parent_id: item.id,
          parent_status: item.status,
          channel: null,
        }),
      )

      const { listValueTranslations: _, ...rest } = item

      return {
        ...rest,
        count: item.listValueTranslations.length,
        fieldOptions,
        __children: children,
      } as TableData
    })
  }, [data, channelOptionsMap, referenceFields])

  return {
    tableData,
    selectOptions: referenceFields,
    isFetching: isTranslationFetching || isReferenceFieldsFetching,
    isLoading: isTranslationLoading || isReferenceFieldsLoading,
  }
}
